import { useCallback, useEffect, useState } from 'react';
import { leadgenApi, type LeadGenGradeConfig, type LeadGenScoringRule } from '@/api/leadgen.api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';
import { usePermissions } from '@/hooks/usePermissions';
export function LeadGenScoringPage() {
  const { hasAny } = usePermissions();
  const canManage = hasAny('LEAD_GEN_MANAGE');
  const [rules, setRules] = useState<LeadGenScoringRule[]>([]);
  const [grades, setGrades] = useState<LeadGenGradeConfig[]>([]);
  const [ruleName, setRuleName] = useState('');
  const [eventType, setEventType] = useState('FORM_SUBMISSION');
  const [scoreDelta, setScoreDelta] = useState('10');
  const [conditionJson, setConditionJson] = useState('{"industry": "technology", "country": "IN"}');

  const load = useCallback(async () => {
    try {
      const [scoring, gradeConfig] = await Promise.all([
        leadgenApi.listScoringRules(),
        leadgenApi.getGradeConfig().catch(() => [] as LeadGenGradeConfig[]),
      ]);
      setRules(scoring);
      setGrades(gradeConfig.sort((a, b) => a.sortOrder - b.sortOrder));
    } catch (err) {
      showApiError(err, 'Failed to load scoring rules');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!ruleName.trim()) return;
    try {
      await leadgenApi.createScoringRule({
        ruleName,
        eventType,
        scoreDelta: Number(scoreDelta),
        conditionJson,
        active: true
      });
      notify.success('Lead scoring rule saved');
      setRuleName('');
      load();
    } catch (err) {
      showApiError(err, 'Failed to create scoring rule');
    }
  };

  return (
    <div className="page-content">
      <PageHeader
        title="Lead Scoring & Grading"
        description="Establish scoring thresholds and assign quality grades (A+, A, B, C, D) based on visitor events and profiles."
        breadcrumbs={[
          { label: 'Org', to: '/org' },
          { label: 'Lead Gen', to: '/lead-gen' },
          { label: 'Scoring Rules' },
        ]}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {canManage && (
        <div className="lg:col-span-2 card p-5 space-y-4">
          <h3 className="section-title">Define Lead Scoring Rule</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="Rule Name" placeholder="e.g. Enterprise Industry Match" value={ruleName} onChange={(e) => setRuleName(e.target.value)} />
            <Input label="Score Delta (Points)" type="number" placeholder="e.g. 15 or -10" value={scoreDelta} onChange={(e) => setScoreDelta(e.target.value)} />
            <Select
              label="Trigger Event"
              value={eventType}
              onChange={(v) => setEventType(v)}
              options={[
                { value: 'FORM_SUBMISSION', label: 'On Form Submission' },
                { value: 'PAGE_VIEW', label: 'On Landing Page View' },
                { value: 'EMAIL_OPEN', label: 'On Campaign Email Opened' },
                { value: 'DOWNLOAD', label: 'On Whitepaper/Asset Download' },
                { value: 'LEAD_CREATED', label: 'On Inbound Lead Intake' },
              ]}
            />
            <Input label="Condition (JSON Match)" placeholder='e.g. {"country": "IN", "companySize": "Enterprise"}' value={conditionJson} onChange={(e) => setConditionJson(e.target.value)} />
          </div>
          <Button className="w-full" onClick={create}>Save Scoring Rule</Button>
        </div>
        )}

        <div className={`card p-5 space-y-4 ${canManage ? '' : 'lg:col-span-3'}`}>
          <h3 className="section-title">Grading Threshold Matrix</h3>
          <div className="space-y-3">
            {grades.map((g) => (
              <div key={g.id} className="flex justify-between items-center p-3 rounded-lg border border-slate-100 bg-slate-50">
                <div>
                  <span className="font-bold text-slate-800 mr-2">{g.gradeCode}</span>
                  <span className="text-xs text-slate-500">
                    {g.maxScore != null ? `${g.minScore} – ${g.maxScore}` : `≥ ${g.minScore}`} points
                  </span>
                </div>
              </div>
            ))}
            {grades.length === 0 && (
              <p className="text-xs text-slate-400">No grade config from server — check LEAD_GEN_SCORING feature.</p>
            )}
          </div>

          <h3 className="section-title pt-4 border-t border-slate-100">Configured Rules</h3>
          <ul className="space-y-3">
            {rules.map(r => (
              <li key={r.id} className="text-xs p-3 rounded-lg border border-slate-100 bg-slate-50 flex justify-between items-start">
                <div>
                  <p className="font-semibold text-slate-700">{r.ruleName}</p>
                  <p className="text-slate-400 mt-1">Delta: {r.scoreDelta > 0 ? `+${r.scoreDelta}` : r.scoreDelta} on {r.eventType}</p>
                </div>
                <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-50 text-emerald-800">Active</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
